/**
 * Role gate for the config-mode UX.
 *
 * Returns `true` if the **current user is an admin** and may therefore
 * toggle config mode, see the CMD/Ctrl+click affordance, and navigate
 * into the admin component-config page. Returns `false` for everyone
 * else — the handler / badge / keyboard shortcut layers read this to
 * decide whether to attach listeners at all.
 *
 * ## Mechanism
 *
 * The project's admin role mechanism is the **admin auth store**
 * (`src/modules/admin/auth/store.ts`). It holds the authenticated admin
 * user in `state.user`, whose shape is {@link AdminUser} and exposes a
 * `roles: string[]` field. Existing call sites (login fixtures,
 * `TenantUsersTab`, `UsersListPage`) treat the string literal `'admin'`
 * as the canonical admin role — the only role explicitly granted in the
 * project. We follow that convention here.
 *
 * Eligibility requires **both**:
 *
 * 1. A populated `user` (someone is logged into the admin area), and
 * 2. `'admin'` present in `user.roles`.
 *
 * Users logged into other areas (e.g., a tenant claimflow session) have
 * no admin auth store entry, so `user` is `null` and this hook returns
 * `false` — exactly what we want: regular users never see the config
 * affordance.
 *
 * ## Why this lives outside the store
 *
 * Keeping the role check out of `configModeStore` means:
 *
 * - Unit tests for the store don't need to stub auth.
 * - The store is reusable from non-admin areas without dependency leaks.
 * - Role logic changes (e.g., adding a `super-admin` role) land in one
 *   file.
 *
 * @example
 * ```ts
 * const eligible = useIsConfigModeEligible();
 * if (!eligible) return null;
 * ```
 */

import { useAdminAuthStore } from '@modules/admin/auth/store';

/**
 * Role name treated as "admin" throughout the project.
 *
 * Exported as a named constant so tests can reference the exact value
 * without repeating the literal, and so a future rename (e.g., to
 * `'platform-admin'`) only happens in one place.
 */
export const ADMIN_ROLE = 'admin';

/**
 * React hook returning `true` when the current user is eligible to use
 * config mode.
 *
 * The subscription is split into two `useAdminAuthStore` calls so each
 * selector returns a stable primitive/array reference — Zustand's
 * shallow compare then avoids re-renders on unrelated store updates
 * (token refresh, spoofing changes, etc.).
 *
 * @returns `true` if the admin auth store has a user and that user has
 *   the `'admin'` role; otherwise `false`.
 */
export function useIsConfigModeEligible(): boolean {
  const user = useAdminAuthStore((state) => state.user);
  const isAuthenticated = useAdminAuthStore((state) => state.isAuthenticated);

  if (!isAuthenticated || !user) {
    return false;
  }

  return user.roles.includes(ADMIN_ROLE);
}
